import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import WorkIcon from '@material-ui/icons/Work';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import GroupIcon from '@material-ui/icons/Group';
import WarningIcon from '@material-ui/icons/Warning';
import FingerprintIcon from '@material-ui/icons/Fingerprint';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      width: 900,
      maxWidth: '95vw',
      height: 700,
      maxHeight: '90vh'
    }
  },
  dialogContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    paddingBottom: theme.spacing(1)
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: '#fff'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  avatar: {
    width: 80,
    height: 80,
    fontSize: '2rem'
  },
  headerInfo: {
    flex: 1
  },
  statusChip: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.dark
  },
  infoGrid: {
    marginTop: theme.spacing(2)
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  infoLabel: {
    fontWeight: 500,
    minWidth: 120
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  tabContent: {
    padding: theme.spacing(2),
    flex: 1,
    overflow: 'auto',
    minHeight: 350
  },
  placeholderBox: {
    padding: theme.spacing(4),
    textAlign: 'center',
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  certChip: {
    margin: theme.spacing(0.5)
  },
  scanRecord: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.grey[100]
    }
  },
  scanTypeChip: {
    fontWeight: 500,
    textTransform: 'uppercase'
  },
  metricCard: {
    height: '100%',
    textAlign: 'center',
    '& .MuiCardContent-root': {
      padding: theme.spacing(2)
    }
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1)
  },
  attendanceGrid: {
    marginBottom: theme.spacing(2)
  }
}));

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

const EmployeeDetailsModalEnhanced = ({ open, onClose, employee }) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = React.useState(0);
  const [loading, setLoading] = useState(false);
  const [employeeData, setEmployeeData] = useState({
    todayShift: null,
    recentShifts: [],
    absences: [],
    incidents: [],
    scanHistory: { scans: [], scansByDay: {} },
    attendanceSummary: null
  });

  // Fetch employee data when modal opens
  useEffect(() => {
    if (open && employee?.id) {
      fetchEmployeeData();
    }
  }, [open, employee?.id]);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_HOST
        ? process.env.REACT_APP_PROTOCOL + '://' + process.env.REACT_APP_HOST
        : window.location.origin;

      // Fetch all employee data in parallel
      const requests = [
        axios.get(`${baseUrl}/api/admin/employee/${employee.id}/today-shift`).catch(() => ({ data: null })),
        axios.get(`${baseUrl}/api/admin/employee/${employee.id}/recent-shifts?days=30`).catch(() => ({ data: [] })),
        axios.get(`${baseUrl}/api/admin/employee/${employee.id}/absences?limit=10`).catch(() => ({ data: [] })),
        axios.get(`${baseUrl}/api/admin/employee/${employee.id}/incidents?limit=10`).catch(() => ({ data: [] })),
        axios.get(`${baseUrl}/api/admin/employee/${employee.id}/scan-history?days=7`).catch(() => ({ data: { scans: [], scansByDay: {} } })),
        axios.get(`${baseUrl}/api/admin/employee/${employee.id}/attendance-summary?days=90`).catch(() => ({ data: null }))
      ];

      const [todayShiftRes, recentShiftsRes, absencesRes, incidentsRes, scanHistoryRes, attendanceRes] = await Promise.all(requests);

      setEmployeeData({
        todayShift: todayShiftRes.data,
        recentShifts: Array.isArray(recentShiftsRes.data) ? recentShiftsRes.data : (recentShiftsRes.data?.shifts || []),
        absences: absencesRes.data || [],
        incidents: incidentsRes.data || [],
        scanHistory: scanHistoryRes.data || { scans: [], scansByDay: {} },
        attendanceSummary: attendanceRes.data
      });
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!employee) return null;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Format date and time helpers
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate tenure
  const calculateTenure = (hireDate) => {
    if (!hireDate) return 'N/A';
    const years = Math.floor((Date.now() - new Date(hireDate)) / (365.25 * 24 * 60 * 60 * 1000));
    return years > 0 ? `${years} year${years > 1 ? 's' : ''}` : 'Less than 1 year';
  };

  // Get scan type color
  const getScanTypeColor = (scanType) => {
    const typeMap = {
      'in': 'primary',
      'out': 'default',
      'break-start': 'secondary',
      'break-end': 'secondary'
    };
    return typeMap[scanType] || 'default';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      className={classes.dialog}
    >
      <DialogTitle className={classes.dialogTitle}>
        Employee Details
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers className={classes.dialogContent}>
        {/* Header Section */}
        <Box className={classes.header}>
          <Avatar 
            src={employee.profilePhotoUrl} 
            className={classes.avatar}
          >
            {!employee.profilePhotoUrl && employee.name?.charAt(0)}
          </Avatar>
          
          <Box className={classes.headerInfo}>
            <Typography variant="h5" gutterBottom>
              {employee.name || 'Unknown Employee'}
            </Typography>
            
            <Box>
              {employee.role && (
                <Chip
                  label={employee.role.charAt(0).toUpperCase() + employee.role.slice(1)}
                  size="small"
                  color="primary"
                  className={classes.statusChip}
                />
              )}
              {employee.clockStatus?.isClockedIn ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Clocked In"
                  size="small"
                  className={classes.statusChip}
                  style={{ backgroundColor: '#4caf50', color: '#fff' }}
                />
              ) : (
                <Chip
                  icon={<CancelIcon />}
                  label="Not Clocked In"
                  size="small"
                  className={classes.statusChip}
                  color="default"
                />
              )}
              {employee.isDriver && (
                <Chip
                  icon={<DirectionsCarIcon />}
                  label="Driver"
                  size="small"
                  className={classes.statusChip}
                  style={{ backgroundColor: '#2196f3', color: '#fff' }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Tabs */}
        <Box className={classes.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Overview" />
            <Tab label="Schedule" />
            <Tab label="Attendance" />
            <Tab label="Performance" />
            <Tab label="History" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box className={classes.tabContent}>
          <TabPanel value={tabValue} index={0}>
            {/* Overview Tab */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Contact Information
                  </Typography>
                  
                  <List dense>
                    {(employee.primaryPhone || employee.secondaryPhone || 
                      (employee.phoneNumbers && employee.phoneNumbers.length > 0)) && (
                      <ListItem>
                        <ListItemIcon>
                          <PhoneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            employee.primaryPhone || 
                            employee.secondaryPhone ||
                            (employee.phoneNumbers && employee.phoneNumbers[0]) ||
                            'No phone'
                          }
                          secondary="Primary Phone"
                        />
                      </ListItem>
                    )}
                    
                    {employee.email && (
                      <ListItem>
                        <ListItemIcon>
                          <EmailIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={employee.email}
                          secondary="Email"
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Employment Details
                  </Typography>
                  
                  <Box className={classes.infoItem}>
                    <AssignmentIndIcon color="primary" />
                    <Typography variant="body2">
                      <span className={classes.infoLabel}>Employee #:</span>
                      {employee.number || employee.id || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box className={classes.infoItem}>
                    <WorkIcon color="primary" />
                    <Typography variant="body2">
                      <span className={classes.infoLabel}>Division:</span>
                      {employee.division || 'Not Assigned'}
                    </Typography>
                  </Box>
                  
                  <Box className={classes.infoItem}>
                    <DateRangeIcon color="primary" />
                    <Typography variant="body2">
                      <span className={classes.infoLabel}>Hire Date:</span>
                      {formatDate(employee.hireDate)}
                    </Typography>
                  </Box>
                  
                  <Box className={classes.infoItem}>
                    <AccessTimeIcon color="primary" />
                    <Typography variant="body2">
                      <span className={classes.infoLabel}>Tenure:</span>
                      {calculateTenure(employee.hireDate)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Current Status
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box className={classes.infoItem}>
                        <GroupIcon color="primary" />
                        <Typography variant="body2">
                          <span className={classes.infoLabel}>Current Crew:</span>
                          {employee.crewName || employee.currentCrew || 'Not Assigned'}
                        </Typography>
                      </Box>
                      
                      {employee.clockStatus?.clockInTime && (
                        <Box className={classes.infoItem}>
                          <AccessTimeIcon color="primary" />
                          <Typography variant="body2">
                            <span className={classes.infoLabel}>Clocked In At:</span>
                            {formatTime(employee.clockStatus.clockInTime)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      {employee.skills && employee.skills.length > 0 && (
                        <Box>
                          <Typography variant="body2" className={classes.infoLabel}>
                            Skills & Certifications:
                          </Typography>
                          <Box>
                            {employee.skills.map((skill, index) => (
                              <Chip
                                key={index}
                                label={skill}
                                size="small"
                                className={classes.certChip}
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Schedule Tab - Real Data */}
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Today's Shift with Scan Records */}
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Today's Shift
                  </Typography>
                  {employeeData.todayShift ? (
                    <Paper style={{ padding: 16, marginBottom: 16 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Clock In:</strong> {formatTime(employeeData.todayShift.clockIn)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Clock Out:</strong> {employeeData.todayShift.clockOut ? 
                              formatTime(employeeData.todayShift.clockOut) : 'Still working'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Hours Worked:</strong> {employeeData.todayShift.hoursWorked || 0} hours
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Crew:</strong> {employeeData.todayShift.crew?.name || 'N/A'}
                          </Typography>
                        </Grid>
                        {employeeData.todayShift.wasTardy && (
                          <Grid item xs={12}>
                            <Chip
                              icon={<WarningIcon />}
                              label="Tardy"
                              size="small"
                              style={{ backgroundColor: '#ff9800', color: '#fff' }}
                            />
                          </Grid>
                        )}
                        
                        {/* Scan Records */}
                        {employeeData.todayShift.scanRecords && employeeData.todayShift.scanRecords.length > 0 && (
                          <Grid item xs={12}>
                            <Divider style={{ margin: '16px 0' }} />
                            <Typography variant="subtitle2" gutterBottom>
                              <FingerprintIcon style={{ verticalAlign: 'middle', marginRight: 4 }} />
                              Scan Records
                            </Typography>
                            {employeeData.todayShift.scanRecords.map((scan, idx) => (
                              <Box key={idx} className={classes.scanRecord}>
                                <Grid container alignItems="center" spacing={1}>
                                  <Grid item xs={4}>
                                    <Chip 
                                      size="small" 
                                      label={scan.scanType}
                                      color={getScanTypeColor(scan.scanType)}
                                      className={classes.scanTypeChip}
                                    />
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Typography variant="body2">
                                      {formatTime(scan.timestamp)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    {scan.isTempBadge && (
                                      <Chip size="small" label="Temp Badge" variant="outlined" />
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No shift recorded for today
                    </Typography>
                  )}
                </Box>

                {/* Recent Shifts */}
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Recent Shifts (Last 30 Days)
                  </Typography>
                  {employeeData.recentShifts.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Clock In</TableCell>
                            <TableCell>Clock Out</TableCell>
                            <TableCell>Hours</TableCell>
                            <TableCell>Crew</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {employeeData.recentShifts.slice(0, 10).map((shift, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {formatDate(shift.clockIn)}
                              </TableCell>
                              <TableCell>
                                {formatTime(shift.clockIn)}
                              </TableCell>
                              <TableCell>
                                {shift.clockOut ? formatTime(shift.clockOut) : 'N/A'}
                              </TableCell>
                              <TableCell>{shift.hoursWorked || 0}h</TableCell>
                              <TableCell>{shift.crew?.name || 'N/A'}</TableCell>
                              <TableCell>
                                {shift.wasTardy && (
                                  <Chip size="small" label="Tardy" color="secondary" style={{ marginRight: 4 }} />
                                )}
                                {shift.overtimeApproved && (
                                  <Chip size="small" label="OT" color="primary" style={{ marginRight: 4 }} />
                                )}
                                {shift.autoClockOut && (
                                  <Chip size="small" label="Auto" variant="outlined" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No recent shifts found
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {/* Attendance Tab - New Comprehensive View */}
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : employeeData.attendanceSummary ? (
              <>
                {/* Attendance Metrics Grid */}
                <Grid container spacing={2} className={classes.attendanceGrid}>
                  <Grid item xs={6} sm={3}>
                    <Card className={classes.metricCard}>
                      <CardContent>
                        <Typography className={classes.metricValue}>
                          {employeeData.attendanceSummary.shifts.total}
                        </Typography>
                        <Typography variant="body2" className={classes.metricLabel}>
                          Total Shifts
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Card className={classes.metricCard}>
                      <CardContent>
                        <Typography className={classes.metricValue} style={{ color: '#4caf50' }}>
                          {employeeData.attendanceSummary.shifts.onTime}
                        </Typography>
                        <Typography variant="body2" className={classes.metricLabel}>
                          On Time
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Card className={classes.metricCard}>
                      <CardContent>
                        <Typography className={classes.metricValue} style={{ color: '#ff9800' }}>
                          {employeeData.attendanceSummary.shifts.tardy}
                        </Typography>
                        <Typography variant="body2" className={classes.metricLabel}>
                          Tardy
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Card className={classes.metricCard}>
                      <CardContent>
                        <Typography className={classes.metricValue}>
                          {employeeData.attendanceSummary.hours.total.toFixed(1)}h
                        </Typography>
                        <Typography variant="body2" className={classes.metricLabel}>
                          Total Hours
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Scan History by Day */}
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Recent Scan Activity (7 Days)
                  </Typography>
                  {Object.keys(employeeData.scanHistory.scansByDay || {}).length > 0 ? (
                    <Paper style={{ padding: 16 }}>
                      {Object.entries(employeeData.scanHistory.scansByDay).map(([day, scans]) => (
                        <Box key={day} style={{ marginBottom: 16 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {day}
                          </Typography>
                          <Box style={{ paddingLeft: 16 }}>
                            {scans.map((scan, idx) => (
                              <Box key={idx} style={{ marginBottom: 4 }}>
                                <Chip 
                                  size="small" 
                                  label={scan.scanType}
                                  color={getScanTypeColor(scan.scanType)}
                                  style={{ marginRight: 8 }}
                                />
                                <Typography variant="body2" component="span">
                                  {formatTime(scan.timestamp)}
                                  {scan.hoursWorked > 0 && ` (${scan.hoursWorked}h worked)`}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                          {day !== Object.keys(employeeData.scanHistory.scansByDay).slice(-1)[0] && (
                            <Divider style={{ marginTop: 8 }} />
                          )}
                        </Box>
                      ))}
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No recent scan activity
                    </Typography>
                  )}
                </Box>

                {/* Absence Summary */}
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Absence Summary (90 Days)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">Total Absences</Typography>
                      <Typography variant="h6">{employeeData.attendanceSummary.absences.total}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">Approved</Typography>
                      <Typography variant="h6">{employeeData.attendanceSummary.absences.approved}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">Pending</Typography>
                      <Typography variant="h6">{employeeData.attendanceSummary.absences.pending}</Typography>
                    </Grid>
                  </Grid>
                  
                  {Object.keys(employeeData.attendanceSummary.absences.types).length > 0 && (
                    <Box style={{ marginTop: 16 }}>
                      <Typography variant="subtitle2" gutterBottom>By Type:</Typography>
                      {Object.entries(employeeData.attendanceSummary.absences.types).map(([type, count]) => (
                        <Chip 
                          key={type}
                          label={`${type}: ${count}`}
                          size="small"
                          variant="outlined"
                          style={{ marginRight: 8, marginBottom: 8 }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No attendance data available
              </Typography>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {/* Performance Tab - Absences and Metrics */}
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Absence History */}
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Absence History
                  </Typography>
                  {employeeData.absences.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Request Date</TableCell>
                            <TableCell>Approved</TableCell>
                            <TableCell>Returned</TableCell>
                            <TableCell>Notes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {employeeData.absences.map((absence, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Chip 
                                  size="small" 
                                  label={absence.type || 'Unknown'}
                                  color="default"
                                />
                              </TableCell>
                              <TableCell>
                                {formatDate(absence.created_at)}
                              </TableCell>
                              <TableCell>
                                {absence.approved_at ? (
                                  <Chip size="small" label="Approved" color="primary" />
                                ) : (
                                  <Chip size="small" label="Pending" color="default" />
                                )}
                              </TableCell>
                              <TableCell>
                                {absence.returned_at ? formatDate(absence.returned_at) : 'N/A'}
                              </TableCell>
                              <TableCell>{absence.notes || 'No notes'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No absence records found
                    </Typography>
                  )}
                </Box>

                {/* Performance Summary */}
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Performance Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Paper style={{ padding: 16, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {employeeData.recentShifts.length}
                        </Typography>
                        <Typography variant="body2">Shifts (30 days)</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper style={{ padding: 16, textAlign: 'center' }}>
                        <Typography variant="h4" style={{ color: '#ff9800' }}>
                          {employeeData.recentShifts.filter(s => s.wasTardy).length}
                        </Typography>
                        <Typography variant="body2">Tardy Days</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            {/* History Tab - Incidents */}
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Incident History */}
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Incident History
                  </Typography>
                  {employeeData.incidents.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Reported By</TableCell>
                            <TableCell>Notes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {employeeData.incidents.map((incident, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Chip 
                                  size="small" 
                                  label={incident.type || 'General'}
                                  style={{ backgroundColor: '#ff5722', color: '#fff' }}
                                />
                              </TableCell>
                              <TableCell>
                                {formatDate(incident.created_at)}
                              </TableCell>
                              <TableCell>{incident.created_by_name || 'Unknown'}</TableCell>
                              <TableCell>
                                <Typography variant="body2" style={{ maxWidth: 300 }}>
                                  {incident.notes || 'No details provided'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No incidents on record
                    </Typography>
                  )}
                </Box>

                {/* Employment Summary */}
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Employment Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Paper style={{ padding: 16 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="textSecondary">Total Incidents</Typography>
                            <Typography variant="h6">{employeeData.incidents.length}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="textSecondary">Total Absences</Typography>
                            <Typography variant="h6">{employeeData.absences.length}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="textSecondary">Employment Status</Typography>
                            <Chip size="small" label="Active" color="primary" />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeDetailsModalEnhanced;